import { Injectable } from '@angular/core';
import {
  BOTTOM_PANEL_HEIGHT_DEFAULT_PERC,
  RIGHT_PANEL_WIDTH_MIN_PX,
  LocationsSkeletonService,
} from '../locations-skeleton.service';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of, switchMap } from 'rxjs';
import * as globalActions from '../../../store/global/core-actions';
import { SkeletonGeneralActions, SkeletonBottomPanelActions, SkeletonRightPanelActions } from './locations-skeleton-actions';
import * as selectors from './locations-skeleton-selectors';
import { toggleLocationDetail, RowActions } from '../location-list/state/location-list-actions';
import * as locationListSelectors from '../location-list/state/location-list-selectors';
import { selectIsCvButtonActive } from '../map/state/map-selectors';
import * as skeletonSelectors from './locations-skeleton-selectors';
import { LocationDetail } from '../enums/location-detail';
import { BottomPanelTabs, RightPanelTabs } from '../locations-skeleton.component';
import { MapActions } from '../map/state/map-actions';

@Injectable()
export class LocationsSkeletonEffects {
  constructor(
      private skeletonService: LocationsSkeletonService,
      private actions$: Actions,
      private store: Store) {
  }

  resizeBottomPanel$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SkeletonBottomPanelActions.resizeBottomPanel),
        // recommended way of incorporating state https://ngrx.io/guide/effects#incorporating-state
        concatLatestFrom(() => this.store
            .select(selectors.selectRightPanelRelativeWidth)),
        map(([action, rightPanelRelativeWidth]) => {
          const bottomPanelRelativeHeight = this.skeletonService.calculateBottomPanelRelativeHeight(
              action.payload.bottomPanelHeight, action.payload.totalHeight);

          if (this.skeletonService.validateBottomPanelHeight(
              action.payload.bottomPanelHeight, action.payload.totalHeight)) {
            this.skeletonService.saveContainerDimensionsToLocalStorage(
                bottomPanelRelativeHeight, rightPanelRelativeWidth);
            return SkeletonBottomPanelActions.setBottomPanelRelativeHeight(
                { bottomPanelRelativeHeight: bottomPanelRelativeHeight });
          } else {
            return SkeletonGeneralActions.initializeContainerDimensionsError();
          }
        }),
    );
  });

  resizeRightPanel$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SkeletonRightPanelActions.resizeRightPanel),
        // recommended way of incorporating state https://ngrx.io/guide/effects#incorporating-state
        concatLatestFrom(() => this.store
            .select(selectors.selectBottomPanelRelativeHeight)),
        map(([action, bottomPanelRelativeHeight]) => {
          const rightPanelRelativeWidth = this.skeletonService.calculateRightPanelRelativeWidth(
              action.rightPanelWidth, action.totalWidth);

          if (this.skeletonService.validateRightPanelWidth(action.rightPanelWidth, action.totalWidth)) {
            this.skeletonService.saveContainerDimensionsToLocalStorage(
                bottomPanelRelativeHeight,
                rightPanelRelativeWidth);
            return SkeletonRightPanelActions.setRightPanelRelativeWidth(
                { rightPanelRelativeWidth: rightPanelRelativeWidth });
          } else {
            return SkeletonGeneralActions.initializeContainerDimensionsError();
          }
        }),
    );
  });

  initializeSkeletonContainerDimensions$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SkeletonGeneralActions.initializeContainerDimensions),
        // recommended way of incorporating state https://ngrx.io/guide/effects#incorporating-state
        concatLatestFrom(() =>
            this.store.select(selectors.selectContainerDimensionsInitialized)),
        // Do not trigger code below if container dimensions have already been initialized
        filter(([_, containerDimensionsInitialized]) => !containerDimensionsInitialized),
        map(([action, _]) => {
          const storedBottomPanelRelativeHeight = this.skeletonService.validateStoredBottomPanelHeight(
              action.totalHeight);
          const storedRightPanelRelativeWidth = this.skeletonService.validateStoredRightPanelWidth(action.totalWidth);

          return {
            height: storedBottomPanelRelativeHeight ?
                storedBottomPanelRelativeHeight : BOTTOM_PANEL_HEIGHT_DEFAULT_PERC,
            width: storedRightPanelRelativeWidth ? storedRightPanelRelativeWidth :
                this.skeletonService.calculateRightPanelRelativeWidth(RIGHT_PANEL_WIDTH_MIN_PX,
                    action.totalWidth),
          };
        }),
        // dispatching multiple actions within 1 effect (may be an anti-pattern though)
        // https://medium.com/@amcdnl/dispatching-multiple-actions-from-ngrx-effects-c1447ceb6b22
        mergeMap((payload: { height: number, width: number }) => [
          SkeletonBottomPanelActions.setBottomPanelRelativeHeight({ bottomPanelRelativeHeight: payload.height }),
          SkeletonRightPanelActions.setRightPanelRelativeWidth({ rightPanelRelativeWidth: payload.width }),
          SkeletonGeneralActions.initializeContainerDimensionsSuccess(),
        ]),
    );
  });

  changeBottomPanelView$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SkeletonBottomPanelActions.userToggleBottomPanel,
            SkeletonBottomPanelActions.userSelectBottomPanelTab),
        switchMap((action) => {
          const getReturnAction = () => {
            switch (action.type) {
              case SkeletonBottomPanelActions.userToggleBottomPanel.type:
                return SkeletonBottomPanelActions.toggleBottomPanel();
              case SkeletonBottomPanelActions.userSelectBottomPanelTab.type:
                return SkeletonBottomPanelActions.selectBottomPanelTab({ payload: action.payload });
            }
          };

          return action.skipUserCheck ? of(null)
                  .pipe(map(() => getReturnAction())) :
              this.skeletonService
                  .canDestroyBottomPanelContainer$()
                  .pipe(map(canDestroy => {
                    if (canDestroy) {
                      return getReturnAction();
                    } else return globalActions.noop();
                  }));
        }),
    );
  });

  changeRightPanelView$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SkeletonRightPanelActions.userToggleRightPanel,
            SkeletonRightPanelActions.userSelectRightPanelTab),
        switchMap((action) => {
          const getReturnAction = () => {
            switch (action.type) {
              case SkeletonRightPanelActions.userToggleRightPanel.type:
                return SkeletonRightPanelActions.toggleRightPanel();
              case SkeletonRightPanelActions.userSelectRightPanelTab.type:
                return SkeletonRightPanelActions.selectRightPanelTab({ payload: action.payload });
            }
          };

          return action.skipUserCheck ? of(null)
                  .pipe(map(() => getReturnAction())) :
              this.skeletonService
                  .canDestroyRightPanelContainer$()
                  .pipe(map(canDestroy => {
                    if (canDestroy) {
                      return getReturnAction();
                    } else return globalActions.noop();
                  }));
        }),
    );
  });

  activateLocationDetail$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(toggleLocationDetail),
        concatLatestFrom(() => [
              this.store.select(locationListSelectors.selectFocusedRowId),
              this.store.select(selectIsCvButtonActive),
              this.store.select(skeletonSelectors.selectRightPanelActive),
              this.store.select(skeletonSelectors.selectBottomPanelActive),
            ],
        ),
        switchMap(([action, focusedRowId, cvButtonActive, rpa, bpa]) => {
          const returnActions = [];

          const populateReturnActions = (skipUserCheck: boolean) => {
            switch (action.detail) {
              case LocationDetail.COMPARE:
                returnActions.push(SkeletonBottomPanelActions.userSelectBottomPanelTab({
                  payload: BottomPanelTabs.COMPARE,
                  skipUserCheck,
                }));
                if (!bpa) {
                  returnActions.push(SkeletonBottomPanelActions.toggleBottomPanel());
                }
                break;
              case LocationDetail.UPDATES:
                returnActions.push(SkeletonBottomPanelActions.userSelectBottomPanelTab({
                  payload: BottomPanelTabs.UPDATES,
                  skipUserCheck,
                }));
                if (!bpa) {
                  returnActions.push(SkeletonBottomPanelActions.toggleBottomPanel());
                }
                break;
              case LocationDetail.EDIT:
                returnActions.push(SkeletonRightPanelActions.userSelectRightPanelTab({
                  payload: RightPanelTabs.EDIT,
                  skipUserCheck,
                }));
                if (!rpa) {
                  returnActions.push(SkeletonRightPanelActions.toggleRightPanel());
                }
                break;
              case LocationDetail.COORDINATE_VALIDATION:
                returnActions.push(SkeletonRightPanelActions.userSelectRightPanelTab({
                  payload: RightPanelTabs.MAP,
                  skipUserCheck,
                }));
                if (!rpa) {
                  returnActions.push(SkeletonRightPanelActions.toggleRightPanel());
                }
                if (!cvButtonActive) {
                  returnActions.push(MapActions.toggleCvButton());
                }
                break;
            }
          };

          if (!focusedRowId || action.id !== focusedRowId) {
            returnActions.push(RowActions.updateFocusedRow({ id: action.id }));
            populateReturnActions(true);

            return this.skeletonService.canDestroyExpansionPanelContainers$().pipe(
                mergeMap((canDestroy) => canDestroy ? returnActions : [globalActions.noop()]),
            );
          } else return of(null).pipe(mergeMap(() => {
            populateReturnActions(false);
            return returnActions;
          }));
        }),
    );
  });

}
